//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useMemo, useRef, useState } from 'react';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  Checkbox,
  CircularProgress,
  debounce,
  IconButton,
  TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';

// === COMPONENTS ===
import Collapsible, { Header } from 'components/Collapsible';
import {Spacer} from 'components/Spacer';
import Styled from 'components/Styled/Styled';
import {emit, on, removeListener, sendMessageData} from 'api/prokey/prokey.websocket';
import {useFile} from '../FileContext/FileContext';
import {getHexAlpha} from 'components/styles.utils';

import useOnVisible from 'components/hooks/useOnVisible/useOnVisible';

//#endregion

const CategoryMemo = React.memo( Category );
const DocumentGroupMemo = React.memo( DocumentGroup );
const DocumentMemo = React.memo( Document );

//-----------------------------------------------------------------
export function DocumentCategoriesList( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const { file_id, is_loading } = useFile();

  const [ categories, setCategories ] = useState([]);
  const [ checked   , setChecked    ] = useState(false);

  const [ search_open , setSearchOpen  ] = useState( false );
  const [ is_searching, setIsSearching ] = useState( false );
  const [ search, setSearch ] = useState('');

  const searchRequest = useMemo( () => debounce((value) => sendSearch(value), 500 ), [] );

  const _tags = [
    // { label: 'My Files'    , group: 'Assigned User' , filter: (file) => true },
    // { label: 'Other Files' , group: 'Assigned User' , filter: (file) => true },
    // { label: 'Complete'    , group: 'Status'        , filter: (file) => true },
    // { label: 'In Progress' , group: 'Status'        , filter: (file) => true },
  ];

  //#endregion

  //#region [STYLES]

  const scrollbar_thin = {
    // scrollbarColor : `${theme.palette.primary.main} ${theme.palette.background.secondary[theme.palette.mode]}`,
    scrollbarWidth : 'thin'
  }
  
  const _bg_pd_blue     = {background  : theme.palette.primary.main}
  const _bg_secondary   = {background  : theme.palette.background.secondary[theme.palette.mode]}
  const _bg_primary     = {background  : theme.palette.background.primary  [theme.palette.mode]}
  const _color_pd_blue  = {color       : theme.palette.primary.main}
  const _border_pd_blue = {borderColor : theme.palette.primary.main}
  const _border_bottom  = {borderBottom:'4px solid'}
  const _no_shrink      = {flexShrink:0}
  const _pointer        = {cursor: 'pointer'};
  const _size_30px      = {height:'30px', width:'30px'}
  const _flex           = {display:'flex'}
  const _center_items   = { justifyContent: 'center', alignItems: 'center'}

  const FILTER_TAGS = {
    id          : 'filter-tags',
    sx          : { width: '100%'},
    multiple    : true,
    options     : _tags,
    groupBy     : (option) => option.group,
    renderInput : (params) => { return <TextField {...params} variant='standard' label="Search" /> },
    onChange    : (event, value) => { props.onFilter( value.map( x => x.filter ) ) },
    name        : 'da_filter_tags_select',
  };

  const TEXT_SEARCH = {
    name      : 'file_categories_filter_search_input',
    sx        : { width: '400px' },
    label     : 'Search...',
    value     : search,
    size      : 'small', 
    autoFocus : true,
    onChange  : onChangeSearch,
  };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    if( is_loading ) return;

    const CATEGORIES = 'FILE_CATEGORIES';
    
    //-----------------------------------------------------------------
    function handleCategories( data ) {
      //-----------------------------------------------------------------
      setIsSearching( false );
      if( !Array.isArray(data) ) {
        console.info('invalid category list', data );
      }
      if( data?.error ) {
        return;
      }
      setCategories( data );
    }

    on( CATEGORIES, handleCategories );

    return () => {
      removeListener( CATEGORIES, handleCategories );
    }
  }, [file_id, is_loading])
  
  useEffect( () => {
    searchRequest(search)
  }, [search, searchRequest])

  //#endregion

  //#region [EVENTS] 

  //-----------------------------------------------------------------
  function onChange_Select( event ) {
    //-----------------------------------------------------------------
    setChecked( !checked );
    sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, is_selected: event.target.checked, is_all: true } )
  }
  
  //-----------------------------------------------------------------
  function sendSearch(value) {
    //-----------------------------------------------------------------
    setIsSearching( true );
    sendMessageData( 'FILE_CATEGORIES_FILTER', { file_id, search: value } )
  }

  //-----------------------------------------------------------------
  function onChangeSearch(event) {
    //-----------------------------------------------------------------
    try {
      const value = event.target.value;
      setSearch( value );
    } catch (error) {
      console.warn(error);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const display_data = is_searching ? <CircularProgress /> : categories?.map( cat => <Styled key={cat.id} styles={[{height: 'fit-content'}]}><CategoryMemo id={cat.id} /></Styled> ) || null;

    const center_items = is_searching ?  { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {};
    return (
      <Styled styles={[{paddingTop: '6px', backgroundColor: theme.palette.background.secondary[theme.palette.mode]}]}>
        {Banner()}
        <Styled styles={[{overflow: 'auto'},scrollbar_thin,{height: 'calc( 100% - 60px )'}, center_items, {backgroundColor: theme.palette.background.primary[theme.palette.mode]}]}>
          {display_data}
        </Styled>
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function Banner() {
    //-----------------------------------------------------------------
    return (
      <Styled styles={[_flex,{height:'60px'},_bg_primary, _center_items]} >
        <Styled styles={[_flex, _center_items, { width: '100px' }, { flexShrink: 0 } ]}>
          <Checkbox checked={checked} onChange={onChange_Select}/>
          Open All
        </Styled>
        <Styled styles={[_flex, _center_items, { flexShrink: 0 }, { width: 'calc( 100% - 100px )' }, { marginLeft: 'auto' }]}>
          <Spacer fill />
          <IconButton onClick={()=>setSearchOpen(!search_open)} > <Search /> </IconButton>
          { search_open ? <TextField  { ...TEXT_SEARCH }/> : null }
          <Spacer width='10px' />
        </Styled>
      </Styled>
    )
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
export function Category( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id } = props;
  
  const theme = useTheme();

  const { file_id } = useFile();

  const [ category, setCategory ] = useState(null);

  const _bg_pd_blue   = { background: theme.palette.primary.main };
  const _bg_primary   = { background: theme.palette.background.primary  [theme.palette.mode] };
  const _bg_secondary = { background: theme.palette.background.secondary[theme.palette.mode] };
  const _tc_pd_white  = { color: theme.palette.primary.contrastText }

  //#endregion

  //#region [STYLES]



  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    const message_type = 'FILE_CATEGORY';
    
    //-----------------------------------------------------------------
    function handleCategory( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setCategory( data );
    }

    on( message_type, handleCategory );

    sendMessageData( 'FILE_REQUEST_CATEGORY', { file_id, category_id: id } )

    return () => {
      removeListener( message_type, handleCategory );
    }
  }, [])

  //#endregion

  //#region [EVENTS] 


  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( category == null ) return null;
    if( category.types === null ) return null;
    if( category.doc_count <= 0 ) return null;

    const child = (
      <Styled key='doc-groups'>
        <Styled styles={[{height:'1rem'}]}/>
        {displayGroups()}
        <Styled styles={[{height:'1rem'}]}/>
      </Styled>
    )
    return (
      <Collapsible
        key={id}
        styles={[]}
        header        = {{ children: <Header height={35} title={category.name} styles={[{fontWeight: 'bold'}]}/>, styles:[ _bg_pd_blue, _tc_pd_white ] }}
        header_closed = {{ children: <Header height={35} title={category.name} styles={[{fontWeight: 'bold'}]}/>, styles:[ _bg_pd_blue, _tc_pd_white ]  }}
        body          = {
          {
            children: child,
            styles:[ {height:'fit-content'} ]
          }
        }
        body_closed   = {{ children: null }}
      />
    );
  }

  //-----------------------------------------------------------------
  function displayGroups() {
    //-----------------------------------------------------------------
    if( category?.types == null ) return null;

    return category.types.order.map( x => {
      const type = category.types.items[x];
      return (
       <DocumentGroupMemo key={x} group={type} /> 
      )
    })
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
export function DocumentGroup( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]
  const { group } = props;
  
  const theme = useTheme();

  const { file_id } = useFile();

  const [ doc_group, setGroup   ] = useState({});
  const [ checked  , setChecked ] = useState(false);

  const _bg_primary   = { background: theme.palette.background.primary  [theme.palette.mode] };
  const _bg_secondary = { background: theme.palette.background.secondary[theme.palette.mode] };

  //#endregion

  //#region [STYLES]

  const _pd_blue = theme.palette.primary.main;
  const _secondary = theme.palette.background.secondary[theme.palette.mode];
  const no_margins    = { margin     : 0        };
  const center_items  = { alignItems : 'center' };
  const _size_v_2rem  = { height     : '2rem'   };
  const grid          = { display    : 'grid'   };
  const subgrid       = { display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1 / -1' };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    const message_type = 'FILE_DOCUMENT_GROUP'+'_'+group.numcode;
    
    //-----------------------------------------------------------------
    function handleDocumentUpdate( data ) {
      //-----------------------------------------------------------------
      setGroup( data )
    }

    on( message_type, handleDocumentUpdate );

    return () => {
      removeListener( message_type, handleDocumentUpdate );
    }
  }, [])

  //#endregion

  //#region [EVENTS] 

  //-----------------------------------------------------------------
  function onChange_Select( event ) {
    //-----------------------------------------------------------------
    // sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: group.documents, is_selected: event.target.checked, is_multi: true } )
  }

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    const is_left_click = event.button === 0;
    const is_multi      = event.ctrlKey;
    const is_comparison = event.shiftKey;

    if(false) {
    } 
      // Comparison Image
      else if( is_left_click && is_comparison && is_multi && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: group.documents, is_selected: true,  is_multi: true } )
    } else if( is_left_click && is_comparison && checked ) {
      setChecked( false );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: group.documents, is_selected: false } )
    } else if( is_left_click && is_comparison && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: group.documents, is_selected: true } )
    }
      // Primary Image
      else if( is_left_click && is_multi && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_DOCUMENT_SELECT'            , { file_id, documents: group.documents, is_selected: true, is_multi: true } )
    } else if( is_left_click && checked ) {
      setChecked( false );
      sendMessageData( 'FILE_DOCUMENT_SELECT'            , { file_id, documents: group.documents, is_selected: false } )
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: group.documents, is_selected: false } )
    } else if( is_left_click && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_DOCUMENT_SELECT'            , { file_id, documents: group.documents, is_selected: true } )
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( group.documents.length <= 0 ) {
      return null;
    }
    return (
      <Collapsible
        key={group.numcode}
        styles={ [
          {height:'fit-content'},
        ]}
        header        = {{ children: <Header height={30} title={group.numcode + ' - ' + group.name} styles={[ {fontWeight: 'bold'}, _bg_primary ]} />, styles:[_bg_primary] }}
        header_closed = {{ children: <Header height={30} title={group.numcode + ' - ' + group.name} styles={[ {fontWeight: 'bold'} ]} /> }}
        body          = {{
          children: 
            <Styled styles={[{height:'fit-content'}]}>
              {displayBody()}
              <Styled key='spacer_2' styles={[{height:'1rem'},]}/>
            </Styled>,
          styles: [{height:'fit-content'}, {paddingLeft: '2rem', paddingRight: '1rem'} ]
        }}
        body_closed   = {{ children: null, styles: [{height: 0}] }}
      />
    )
  }

  //-----------------------------------------------------------------
  function displayBody() {
    //-----------------------------------------------------------------
    if( group.documents.length <= 0 ) {
      return <Styled styles={[{display: 'flex'}]}><Spacer width='2rem' />No documents found.</Styled>
    }

    const grid_columns = { columnGap : '12px', gridTemplateColumns: `40px 50px 150px repeat( ${group.headers  .length-2||1}, minmax( 60px, auto ) )` };
    const grid_rows    = {    rowGap :  '2px', gridTemplateRows   : ` min(30px,auto) repeat( ${group.documents.length  ||1}, minmax( 30px, auto ) )` };
    const docHeader = (
      <Styled styles={[ subgrid, {cursor:'pointer'}, _bg_primary, { marginRight: '12px'}, { alignItems: 'start' }, ]}>
        <Styled styles={[{display:'flex'}, _size_v_2rem]}>
          <Checkbox checked={checked} onMouseDown={onMouseDown} />
          <Spacer />
        </Styled>
        {group.headers.map( header => <Styled key={header} styles={[{fontWeight: 'bold'}, { wordBreak: 'break-word' }]}>{header}</Styled> )}
      </Styled>
    )

    const documentList = group.documents.map( doc_id => {
      return <DocumentMemo key={doc_id} id={doc_id} columns={group.headers?.length-3} />
    });

    return (
      <Styled styles={[ { display: 'grid' }, grid_rows, grid_columns, { cursor: 'auto' }, { overflow: 'auto' }, { justifyContent: 'start' } ]}>
        {docHeader   }
        {documentList}
        <Styled styles={[{ height: '12px' }]} />
      </Styled>
    )
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
export function Document( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id, columns } = props;
  
  const theme = useTheme();

  const { file_id } = useFile();

  const [ document, setDocument ] = useState();

  const [checked, setChecked] = useState(false);
  const [focused, setFocused] = useState(false);
  const [is_comp, setIsComp ] = useState(false);

  const ref = useRef();
  const [is_visible] = useOnVisible(ref)

  //#endregion

  //#region [STYLES]

  const no_margins    = { margin     : 0        };
  const center_items  = { alignItems : 'center' };
  const _size_v_2rem  = { height     : '2rem'   };
  const grid          = { display    : 'grid'   };
  const _bg_pd_blue   = { background : theme.palette.primary.main                             };
  const _bg_primary   = { background : theme.palette.background.primary  [theme.palette.mode] };
  const _bg_secondary = { background : theme.palette.background.secondary[theme.palette.mode] };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    const DOCUMENT_PREVIEW = 'FILE_DOCUMENT_PREVIEW';
    
    //-----------------------------------------------------------------
    function handleDocumentPreview( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setDocument( data )
    }
    //-----------------------------------------------------------------
    function handleDocument( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      if( data.is_selected == null ) return;
      setChecked( data.is_selected );
      setIsComp ( false );
    }
    //-----------------------------------------------------------------
    function handleComparisonDocument( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      if( data.is_selected == null ) return;
      setChecked( data.is_selected );
      setIsComp ( true );
    }

    on( DOCUMENT_PREVIEW           , handleDocumentPreview    );
    on( 'FILE_DOCUMENT'            , handleDocument           );
    on( 'FILE_COMPARISON_DOCUMENT' , handleComparisonDocument );

    return () => {
      removeListener( DOCUMENT_PREVIEW           , handleDocumentPreview    );
      removeListener( 'FILE_DOCUMENT'            , handleDocument           );
      removeListener( 'FILE_COMPARISON_DOCUMENT' , handleComparisonDocument );
    }
  }, [])

  useEffect( () => {
    if( !checked ) {
      if( focused ) {
        setFocused( false );
      }
    };

    function handleFocus(data) {
      if( data.id !== id && focused && !is_comp ) {
        setFocused( false );
      };
      if( data.id !== id ) return;
      setFocused( true );
      ref.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
      ref.current.focus();
    }

    function handleComparisonFocus(data) {
      if( data.id !== id && focused && is_comp ) {
        setFocused( false );
      };
      if( data.id !== id ) return;
      setFocused( true );
      ref.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
      ref.current.focus();
    }

    on( 'FILE_DOCUMENT_FOCUS'           , handleFocus           );
    on( 'FILE_COMPARISON_DOCUMENT_FOCUS', handleComparisonFocus );

    return () => {
      removeListener( 'FILE_DOCUMENT_FOCUS'           , handleFocus           );
      removeListener( 'FILE_COMPARISON_DOCUMENT_FOCUS', handleComparisonFocus );
    }
  }, [checked, focused, is_comp] );
  
  useEffect( () => {
    if( !is_visible ) return;
    sendMessageData( 'FILE_REQUEST_DOCUMENT_PREVIEW', { file_id, document_id: id } );
  }, [id,is_visible] )

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    const is_left_click = event.button === 0;
    const is_comparison = event.shiftKey;
    const is_multi      = event.ctrlKey;

    if( is_left_click && is_comparison ) {
      event.preventDefault();
    }

    if( false ) {
    } else if( is_left_click && is_comparison && is_multi ) {
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: !checked,  is_multi: true } )
    } else if( is_left_click && is_comparison && !checked ) {
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: true } )
    } else if( is_left_click && is_multi ) {
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: !checked,  is_multi: true } )
    } else if( is_left_click && !checked ) {
      sendMessageData( 'FILE_DOCUMENT_SELECT', { file_id, documents: [id], is_selected: true } )
    } else if( is_left_click &&  checked && is_comp ) {
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_FOCUS', { file_id, document_id: id } );
    } else if( is_left_click &&  checked ) {
      sendMessageData( 'FILE_DOCUMENT_FOCUS', { file_id, document_id: id } );
    }
  }

  //-----------------------------------------------------------------
  function onMouseDown_Checkbox(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
    event.stopPropagation();
    // Left mouse button
    const is_left_click = event.button === 0;
    const is_comparison = event.shiftKey;
    const is_multi      = event.ctrlKey;
    
    if( false ) {
    } 
      // Comparison Image
      else if( is_left_click && is_comparison && is_multi && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: [id], is_selected: true,  is_multi: true } )
    } else if( is_left_click && is_comparison && checked ) {
      setChecked( false );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: [id], is_selected: false } )
    } else if( is_left_click && is_comparison && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: [id], is_selected: true } )
    }
      // Primary Image
      else if( is_left_click && is_multi && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_DOCUMENT_SELECT'            , { file_id, documents: [id], is_selected: true, is_multi: true } )
    } else if( is_left_click && checked &&  is_comp ) {
      setChecked( false );
      sendMessageData( 'FILE_COMPARISON_DOCUMENT_SELECT' , { file_id, documents: [id], is_selected: false } )
    } else if( is_left_click && checked && !is_comp ) {
      setChecked( false );
      sendMessageData( 'FILE_DOCUMENT_SELECT'            , { file_id, documents: [id], is_selected: false } )
    } else if( is_left_click && !checked ) {
      setChecked( true );
      sendMessageData( 'FILE_DOCUMENT_SELECT'            , { file_id, documents: [id], is_selected: true } )
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const subgrid = { gridTemplateColumns: 'subgrid', gridColumn: '1 / -1' };
    let bg = {}
    if( checked ) {
      bg.background  = is_comp ? theme.palette.secondary.main :  '#ffa500';
      bg.background += focused ? getHexAlpha( 80 ) : getHexAlpha( 20 );
    }
    return (
      <Styled ref={ref} styles={[ { display: 'grid' }, subgrid, bg, no_margins, { cursor:'pointer' }, { alignItems: 'start' } ]}  onMouseDown={onMouseDown} >
        { 
          document?.display_fields == null ? null
          : (
            <>
              <Styled styles={[ { display:'flex' }, _size_v_2rem ]}>
                <Checkbox checked={checked} onMouseDown={onMouseDown_Checkbox} />
                <Spacer />
              </Styled>
              { document.display_fields.map( (data,index) => <Styled key={index} styles={[ { fontWeight: 'bold' }, { wordBreak: 'break-word' } ]}>{data}</Styled> ) }
            </>
          )
        }
      </Styled>
    )
  }

  //#endregion

  return display();
}